import React, { createContext, useContext, useEffect, useMemo, useReducer } from "react";

const CartContext = createContext(null);
const STORAGE_KEY = "session_cart";

// Support both id/_id from API
const getId = (p) => p?.id ?? p?._id;

function reducer(state, action) {
  switch (action.type) {
    case "INIT":
      return Array.isArray(action.payload) ? action.payload : [];

    case "ADD": {
      const item = action.payload;
      const id = getId(item);
      if (!id) return state;
      const existing = state.find((x) => getId(x) === id);
      if (existing) {
        return state.map((x) =>
          getId(x) === id ? { ...x, qty: (x.qty ?? 1) + (item.qty ?? 1) } : x
        );
      }
      return [...state, { ...item, qty: item.qty ?? 1 }];
    }

    case "INC": {
      const id = action.payload;
      return state.map((x) =>
        getId(x) === id ? { ...x, qty: (x.qty ?? 1) + 1 } : x
      );
    }

    case "DEC": {
      const id = action.payload;
      return state.map((x) =>
        getId(x) === id ? { ...x, qty: Math.max(1, (x.qty ?? 1) - 1) } : x
      );
    }

    case "REMOVE":
      return state.filter((it) => getId(it) !== action.payload);

    case "CLEAR":
      return [];

    default:
      return state;
  }
}

export function CartProvider({ children }) {
  const [cart, dispatch] = useReducer(reducer, []);

  // Load from sessionStorage on mount
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (raw) dispatch({ type: "INIT", payload: JSON.parse(raw) });
    } catch {}
  }, []);

  // Persist to sessionStorage whenever cart changes
  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(cart));
    } catch {}
  }, [cart]);

  // (Optional) sync across tabs in same session
  useEffect(() => {
    const handler = (e) => {
      if (e.key === STORAGE_KEY && e.storageArea === sessionStorage) {
        try {
          dispatch({ type: "INIT", payload: JSON.parse(e.newValue || "[]") });
        } catch {}
      }
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, []);

  // Derived values
  const totalItems = useMemo(
    () => cart.reduce((sum, i) => sum + (i.qty ?? 1), 0),
    [cart]
  );
  const subtotal = useMemo(
    () => cart.reduce((sum, i) => sum + Number(i.price || 0) * (i.qty ?? 1), 0),
    [cart]
  );

  const api = useMemo(
    () => ({
      cart,
      totalItems,           // use this for the navbar badge
      subtotal,             // use on cart page
      addToCart: (item) => dispatch({ type: "ADD", payload: item }),
      inc: (id) => dispatch({ type: "INC", payload: id }),
      dec: (id) => dispatch({ type: "DEC", payload: id }),
      removeFromCart: (id) => dispatch({ type: "REMOVE", payload: id }),
      clearCart: () => dispatch({ type: "CLEAR" }),
      getId,                // helper if you need it in UI
    }),
    [cart, totalItems, subtotal]
  );

  return <CartContext.Provider value={api}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}
